import { z } from "zod";

export const dashboardWorkflowStatusSchema = z.enum([
  "Draft",
  "Submitted",
  "Approved",
  "Rejected",
  "Locked",
  "Exported",
  "Exception",
]);

export const payPeriodProgressStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  state: z.enum(["pending", "active", "complete"]),
});

export const dashboardSiteActivitySchema = z.object({
  siteId: z.string().uuid(),
  siteCode: z.string(),
  siteName: z.string(),
  totalHours: z.number(),
  overtimePercent: z.number(),
  activeWorkers: z.number(),
  equipmentInUse: z.number(),
  status: dashboardWorkflowStatusSchema,
});

export const smartExceptionCategorySchema = z.enum([
  "missing_entries",
  "overtime_spike",
  "equipment_missing_hours",
  "rule_conflicts",
  "manual_overrides",
]);

export const dashboardSmartExceptionSchema = z.object({
  category: smartExceptionCategorySchema,
  categoryLabel: z.string(),
  count: z.number().int(),
  severity: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]),
  items: z.array(
    z.object({
      id: z.string(),
      message: z.string(),
      targetType: z.string(),
    }),
  ),
});

export const dashboardDataSchema = z.object({
  greeting: z.object({
    userName: z.string(),
    companyName: z.string(),
  }),
  currentPayPeriod: z
    .object({
      id: z.string().uuid(),
      label: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      status: z.enum(["OPEN", "IN_REVIEW", "APPROVED", "LOCKED", "EXPORTED"]),
    })
    .nullable(),
  kpis: z.object({
    totalHoursToday: z.number(),
    regularHours: z.number(),
    overtimeHours: z.number(),
    doubleTimeHours: z.number(),
    estimatedGrossPay: z.number().nullable(),
    activeWorkers: z.number().int(),
    equipmentInUse: z.number().int(),
    equipmentCost: z.number().nullable(),
    pendingApprovals: z.number().int(),
    exceptionsNeedingReview: z.number().int(),
  }),
  siteActivity: z.array(dashboardSiteActivitySchema),
  payPeriodProgress: z.array(payPeriodProgressStepSchema),
  crewStatus: z.object({
    onSite: z.number().int(),
    missingEntries: z.number().int(),
    offSite: z.number().int(),
    unassigned: z.number().int(),
  }),
  smartExceptions: z.array(dashboardSmartExceptionSchema),
  currency: z.string(),
  asOfDate: z.string(),
});

export const dashboardResponseSchema = z.object({
  data: dashboardDataSchema,
});

export type DashboardWorkflowStatus = z.infer<typeof dashboardWorkflowStatusSchema>;
export type DashboardSiteActivity = z.infer<typeof dashboardSiteActivitySchema>;
export type DashboardSmartException = z.infer<typeof dashboardSmartExceptionSchema>;
export type DashboardData = z.infer<typeof dashboardDataSchema>;
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;
